/**
 * Outstanding orders card — sidebar summary of a patient's orders that still
 * require action (anything that would land in the checkout or remote dispensing
 * queue). Each order is shown in a stripped-down form: product name plus status
 * and payment badges. A button mounts the full checkout screen in a dialog so
 * staff can prepare, charge, and dispense without leaving the patient page.
 */

import { ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { formatClinicPrice, getProductDisplayInfo } from '@snootsvet/shared'

import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { Dialog, DialogContent } from '@/frontend/components/ui/dialog'
import { CheckoutScreen } from '@/frontend/features/orders/checkout-screen/checkout-screen'
import { useListPatientOrders } from '@/frontend/features/orders/hooks/use-list-patient-orders'
import { OrderStatusBadge, PaymentBadge } from '@/frontend/features/orders/order-badges'
import { isOutstandingOrder } from '@/frontend/features/orders/orders.utils'

/** Sidebar card showing the patient's outstanding orders with a checkout shortcut. */
export function OutstandingOrdersCard({ patientId }: { patientId: string }) {
  const { data: orders } = useListPatientOrders(patientId)
  const [checkoutOpen, setCheckoutOpen] = useState(false)

  const outstanding = orders?.filter(isOutstandingOrder) ?? []

  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => setCheckoutOpen(true)}>
        <CardTitle>Orders</CardTitle>
        <CardAction>
          <ChevronRight className="size-4 text-muted-foreground" />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-2">
        {outstanding.length === 0 && (
          <div className="text-muted-foreground">No outstanding orders</div>
        )}
        {outstanding.map((order) => {
          const { name, quantityLabel } = getProductDisplayInfo(order)
          return (
            <div key={order.id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="truncate" title={name}>
                  {name}
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <OrderStatusBadge status={order.status} size="xs" />
                  <PaymentBadge order={order} size="xs" />
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-muted-foreground">× {quantityLabel}</div>
                <div className="tabular-nums">
                  {formatClinicPrice(order.totalPrice, order.clinicId)}
                </div>
              </div>
            </div>
          )
        })}
      </CardContent>

      {/* Checkout dialog — mirrors the checkout and remote dispensing pages. */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-7xl p-4">
          {checkoutOpen && <CheckoutScreen patientId={patientId} />}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
